class SearchDrawer extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="text"]');
    this.reset = this.querySelector('.search_drawer_reset');
    this.emptyWrapper = this.querySelector('.empty_search_drawer');
    this.resultWrapper = this.querySelector('.result_search_drawer');
    this.resultInner = this.resultWrapper.querySelector('.result_search_inner_popup');
  }

  connectedCallback() {
    if (this.input) {
      this.input.addEventListener('input', this.debounce(this.handleInput.bind(this), 300));
    }
    this.reset?.addEventListener('click', this.handleReset.bind(this));

    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();

        const q = this.input.value.trim();
        if (q.length > 0) {
          const searchUrl = new URL(`${window.Shopify.routes.root}search`, window.location.origin);
          searchUrl.searchParams.set('q', q);
          searchUrl.searchParams.set('options[prefix]', 'last');
          if (document.documentElement.hasAttribute('data-modish-available-catalog')) {
            searchUrl.searchParams.set('filter.v.availability', '1');
            searchUrl.searchParams.set('options[unavailable_products]', 'hide');
          }
          window.location.href = searchUrl.toString();
        }
      }
    });
  }

  debounce(fn, delay) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), delay);
    };
  }  

  handleReset(e) {
    e.preventDefault();
    this.input.value = "";
    this.showEmpty(true);
  }

  handleInput() {
    // --------------------------------------
    // --------------------------------------
    this.input.value = this.input.value.replace(/[^\p{L}\p{N} _-]/gu, "");
    const q = this.input.value.trim();
    // --------------------------------------

    if (q.length === 0) {
      this.showEmpty(true);
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();

    const predictiveUrl = new URL(
      window.routes?.predictive_search_url || `${window.Shopify.routes.root}search/suggest`,
      window.location.origin
    );
    predictiveUrl.searchParams.set('q', q);
    predictiveUrl.searchParams.set('resources[type]', 'product,collection,article,page');
    if (document.documentElement.hasAttribute('data-modish-available-catalog')) {
      predictiveUrl.searchParams.set('resources[options][unavailable_products]', 'hide');
    }
    predictiveUrl.searchParams.set('section_id', 'catalog-predictive-search-data');

    fetch(predictiveUrl.toString(), { signal: this.abortController.signal })
      .then(res => {
        if (!res.ok) throw new Error(`Predictive search request failed: ${res.status}`);
        return res.text();
      })
      .then(html => {
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const dataNode = documentFragment.querySelector('[data-catalog-predictive-search-data]');
        const productCardsNode = documentFragment.querySelector('[data-catalog-predictive-product-cards]');
        if (!dataNode) throw new Error('Predictive search data was not returned');
        this.renderResults(JSON.parse(dataNode.textContent), productCardsNode?.innerHTML || '');
      })
      .catch(err => {
        if (err.name === 'AbortError') return;
        console.error('Error fetching results:', err);
        this.renderResults({ products: [], collections: [], articles: [], pages: [], queries: [] });
      });
  }

  showEmpty(state) {
    this.emptyWrapper.style.display = state ? "flex" : "none";
    this.resultWrapper.style.display = state ? "none" : "block";
  }

  renderResults(results, productCardsHtml = '') {
    const { products = [], collections = [], articles = [], pages = [], queries = [] } = results;
    const strings = window.themeStrings?.predictiveSearch || {};

    this.showEmpty(false);
    this.resultInner.innerHTML = "";

    const addGroup = (title, html, modifier = '') => {
      const wrap = document.createElement('div');
      wrap.classList.add('search_group');
      if (modifier) wrap.classList.add(`search_group--${modifier}`);
      wrap.innerHTML = `<h6>${title}</h6>${html}`;
      this.resultInner.appendChild(wrap);
    };

    if (queries.length) {
      addGroup(strings.suggestions || "Suggestions", queries.map(q => `
        <div class="query_card">
          <p><a href="${q.url}" aria-label="${q.text}">${q.styled_text}</a></p>
        </div>
      `).join(""));
    }

    if (collections.length) {
      addGroup(strings.collections || "Collections", collections.map(c => `
        <div class="search_result_collection_card">
          <p><a href="${c.url}" aria-label="${c.title}">${c.title}</a></p>
        </div>
      `).join(""));
    }

    if (productCardsHtml.trim()) {
      addGroup(strings.products || "Products", productCardsHtml, 'products');
    } else if (products.length) {
      addGroup(strings.products || "Products", products.map(p => `
        <div class="search_result_product_card">
          <div class="search_result_product_card_img ratio" style="--aspect-ratio: 90%;">
            <a href="${p.url}" aria-label="${p.title}"><img src="${p.featured_image?.url}" alt="${p.title}"></a>
          </div>
          <div class="search_result_product_card_info">
            <p><a href="${p.url}" aria-label="${p.title}">${p.title}</a></p>
            <div class="search_result_product_price">
              <span>${Shopify.formatMoney(p.price_min)}</span>
              ${p.compare_at_price_min > p.price_min ? `<s>${Shopify.formatMoney(p.compare_at_price_min)}</s>` : ""}
            </div>
          </div>
        </div>
      `).join(""), 'products');
    }

    if (pages.length) {
      addGroup(strings.pages || "Pages", pages.map(pg => `
        <div class="search_result_page_card">
          <p><a href="${pg.url}" aria-label="${pg.title}">${pg.title}</a></p>
        </div>
      `).join(""));
    }

    if (articles.length) {
      addGroup(strings.articles || "Articles", articles.map(a => `
        <div class="search_result_article_card">
          <div class="search_result_article_card_img ratio" style="--aspect-ratio: 90%;">
            <a href="${a.url}" aria-label="${a.title}"><img src="${a.image}" alt="${a.title}"></a>
          </div>
          <div class="search_result_article_info">
            <p>${a.title}</p>
            <a href="${a.url}" aria-label="${a.title}">${strings.readMore || "Read more"}</a>
          </div>
        </div>
      `).join(""));
    }    

    if (!queries.length && !collections.length && !products.length && !articles.length && !pages.length) {
      this.resultInner.innerHTML = `<h6 class="empty_results_heading">${strings.noResults || "No results found for your search."}</h6>`;
    }

    window.initProductCardSliders?.(this.resultInner);
  }
}

customElements.define('search-drawer', SearchDrawer);
